// File: frontend/src/pages/Home.jsx

/*
  Flywheel hub. Four section tiles, plus a Changed Lives card.
  Tiles and summary links follow the viewer's existing permissions.
  The card shows year-to-date figures from /api/reports/changed-lives.
*/
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS, visibleFlywheelSections } from '../config/permissions';
import { get } from '../api/client';
import './Home.css';

const WEEKLY_AVERAGES = [
  { key: 'largeGroupAverage', label: 'Large Group' },
  { key: 'openShareAverage', label: 'Open Share' },
];

const YEAR_TOTALS = [
  { key: 'blueChips', label: 'Blue chips' },
  { key: 'stepStudyGraduates', label: 'Step Study grads' },
  { key: 'baptisms', label: 'Baptisms' },
];

function formatStat(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.round(value).toLocaleString('en-US');
}

function StatList({ stats, values }) {
  return (
    <dl className="changed-lives-stats">
      {stats.map((stat) => {
        const value = formatStat(values?.[stat.key]);
        return (
          <div key={stat.key}>
            <dt>{stat.label}</dt>
            <dd>{value == null ? <span aria-label="No data">—</span> : value}</dd>
          </div>
        );
      })}
    </dl>
  );
}

const Home = () => {
  const { hasPermission, isAdmin } = usePermissions();
  const access = { hasPermission, isAdmin, hasAssignedCheckup: false };
  const sections = visibleFlywheelSections(access);
  const canSeeReports = hasPermission(PERMISSIONS.DASHBOARD);
  const [statsState, setStatsState] = useState({ status: 'idle', stats: null });

  useEffect(() => {
    if (!canSeeReports) return undefined;
    let cancel = false;
    setStatsState({ status: 'loading', stats: null });
    get('/api/reports/changed-lives')
      .then(({ ok, data }) => {
        if (cancel) return;
        setStatsState({
          status: ok && data ? 'ready' : 'error',
          stats: ok && data ? data : null,
        });
      })
      .catch(() => {
        if (!cancel) setStatsState({ status: 'error', stats: null });
      });
    return () => {
      cancel = true;
    };
  }, [canSeeReports]);

  let summaryText = 'Changed lives are the center of this ministry. Open a section to see the work you can open.';
  if (canSeeReports && statsState.status === 'loading') {
    summaryText = 'Loading this year so far.';
  } else if (canSeeReports && statsState.status === 'error') {
    summaryText = 'This year\u2019s figures could not be loaded.';
  }

  const showStats = canSeeReports && statsState.status === 'ready';
  const totalsLabel = statsState.stats?.year ? `${statsState.stats.year} totals` : 'Totals';

  return (
    <div className="hub-page">
      <header className="hub-intro">
        <p className="hub-kicker">Ministry Data Portal</p>
        <h1 className="hub-title">Healed to Help</h1>
        <p className="hub-lead">Four parts of the ministry, with changed lives at the center.</p>
      </header>

      <section className="changed-lives" aria-labelledby="changed-lives-heading">
        <div className="changed-lives-top">
          <div className="changed-lives-copy">
            <h2 id="changed-lives-heading">Changed Lives</h2>
            {!showStats && <p>{summaryText}</p>}
          </div>
          <div className="mini-wheel" aria-hidden="true">
            <span className="mini-wheel-letter mini-wheel-a">A</span>
            <span className="mini-wheel-letter mini-wheel-b">B</span>
            <span className="mini-wheel-letter mini-wheel-c">C</span>
            <span className="mini-wheel-letter mini-wheel-d">D</span>
            <span className="mini-wheel-hub">Lives</span>
          </div>
        </div>

        {showStats && (
          <div className="changed-lives-groups">
            <div>
              <h3 className="changed-lives-group-label">Weekly averages</h3>
              <StatList stats={WEEKLY_AVERAGES} values={statsState.stats} />
            </div>
            <div>
              <h3 className="changed-lives-group-label">{totalsLabel}</h3>
              <StatList stats={YEAR_TOTALS} values={statsState.stats} />
            </div>
          </div>
        )}

        {canSeeReports && (
          <div className="changed-lives-actions">
            <Link to="/dashboard">Reports Summary</Link>
            <Link to="/graphs">Graphs</Link>
          </div>
        )}
      </section>

      {sections.length > 0 ? (
        <nav className="flywheel-grid" aria-label="Ministry sections">
          {sections.map((section) => (
            <Link key={section.id} to={section.path} className="flywheel-tile">
              <span className="letter-badge letter-badge-lg">{section.letter}</span>
              <span className="flywheel-tile-copy">
                <span className="flywheel-tile-title">{section.title}</span>
                <span className="flywheel-tile-desc">{section.description}</span>
              </span>
            </Link>
          ))}
        </nav>
      ) : (
        <p className="hub-empty">
          You don&apos;t have access to any sections yet. Contact your administrator to get permissions.
        </p>
      )}
    </div>
  );
};

export default Home;
